/**
 * Auth Store - Zustand State Management
 * 
 * Manages user authentication state.
 * 
 * @validates Requirements 3.1, 3.2, 3.3, 3.6 - Authentication flow
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User, UserRole } from '@budgira/shared';
import { authService, LoginResponse, getToken, clearTokens } from '../services/auth.service';
import { useAccountStore } from './accountStore';
import { useOperationStore } from './operationStore';
import { useSchedulerStore } from './schedulerStore';
import { useUserSettingsStore } from './userSettingsStore';
import { useProjectStore } from './projectStore';

// Storage type key to track which storage is being used (same as auth.service.ts)
const AUTH_STORAGE_TYPE_KEY = 'budgira_auth_type';

/**
 * Get the appropriate storage based on remember me preference
 */
function getAuthStorage(): Storage {
  const storageType = localStorage.getItem(AUTH_STORAGE_TYPE_KEY);
  return storageType === 'local' ? localStorage : sessionStorage;
}

/**
 * Custom storage that uses localStorage or sessionStorage based on remember me preference
 */
const authStorage = {
  getItem: (name: string): string | null => {
    // Check both storages - localStorage first (remember me), then sessionStorage
    let value = localStorage.getItem(name);
    if (value) return value;
    value = sessionStorage.getItem(name);
    return value;
  },
  setItem: (name: string, value: string): void => {
    // Use the same storage as the tokens
    const storage = getAuthStorage();
    // Clear both to avoid conflicts
    localStorage.removeItem(name);
    sessionStorage.removeItem(name);
    storage.setItem(name, value);
  },
  removeItem: (name: string): void => {
    localStorage.removeItem(name);
    sessionStorage.removeItem(name);
  },
};

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  requiresTwoFactor: boolean;
  requiresEmail2FA: boolean;
  requiresWorkspaceSelection: boolean;
  availableWorkspaces: Array<{
    id: string;
    name: string;
    type: string;
    role: string;
    isDefault: boolean;
    ownerEmail?: string;
    displayName?: string;
  }>;
  userId: string | null;
  email2FASessionId: string | null;
  rememberMe: boolean;
  
  // Actions
  login: (email: string, password: string, rememberMe?: boolean) => Promise<boolean>;
  verify2FA: (code: string) => Promise<boolean>;
  verifyEmail2FA: (code: string) => Promise<{ success: boolean; error?: string; attemptsRemaining?: number; isLocked?: boolean }>;
  resendEmail2FA: () => Promise<{ success: boolean; error?: string }>;
  selectWorkspace: (workspaceId: string) => Promise<boolean>;
  logout: () => Promise<void>;
  register: (email: string, password: string, language?: 'fr' | 'en') => Promise<boolean>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  resetEmail2FAState: () => void;
  resetWorkspaceSelectionState: () => void;
  refreshUser: () => Promise<User | null>;
  updateSharedAccess: (sharedAccess: User['sharedAccess']) => void;
  
  // Computed
  isPremium: () => boolean;
  isAdmin: () => boolean;
  isSecondaryUser: () => boolean;
  isOnSharedWorkspace: () => boolean;
  canManageCategories: () => boolean;
  canManagePaymentMethods: () => boolean;
  canManageSchedulers: () => boolean;
  canManageSharedAccess: () => boolean;
  canUseReconciliation: () => boolean;
  // New feature permission checks
  canViewCategories: () => boolean;
  canViewPaymentMethods: () => boolean;
  canViewCheckbooks: () => boolean;
  canManageCheckbooks: () => boolean;
  canViewProjects: () => boolean;
  canManageProjects: () => boolean;
  canViewReconciliation: () => boolean;
  canManageReconciliation: () => boolean;
  canViewStatements: () => boolean;
  canManageStatements: () => boolean;
  canViewBudgets: () => boolean;
  canManageBudgets: () => boolean;
  // API Keys permissions
  canViewApiKeys: () => boolean;
  canManageApiKeys: () => boolean;
  // Alerts permissions
  canViewAlerts: () => boolean;
  canManageAlerts: () => boolean;
  canViewOwnerAlerts: () => boolean;
  // Counterparties (tiers) permissions
  canViewCounterparties: () => boolean;
  canManageCounterparties: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      requiresTwoFactor: false,
      requiresEmail2FA: false,
      requiresWorkspaceSelection: false,
      availableWorkspaces: [],
      userId: null,
      email2FASessionId: null,
      rememberMe: false,

      login: async (email: string, password: string, rememberMe: boolean = false): Promise<boolean> => {
        set({ isLoading: true, error: null, rememberMe });
        try {
          const response = await authService.login({ email, password, rememberMe });
          
          if (!response.success) {
            set({ error: response.error?.message || 'Login failed', isLoading: false });
            return false;
          }

          const data = response.data as LoginResponse;
          
          // Check for TOTP 2FA requirement (backend uses requires2FA)
          if (data.requires2FA) {
            set({
              requiresTwoFactor: true,
              userId: data.user?.id || null,
              isLoading: false,
            });
            return true;
          }

          // Check for email 2FA requirement
          if (data.requiresEmail2FA && data.userId && data.sessionId) {
            set({
              requiresEmail2FA: true,
              userId: data.userId,
              email2FASessionId: data.sessionId,
              isLoading: false,
            });
            return true;
          }

          // Check for workspace selection requirement
          if (data.requiresWorkspaceSelection && data.workspaces) {
            set({
              requiresWorkspaceSelection: true,
              availableWorkspaces: data.workspaces,
              user: data.user,
              isAuthenticated: true, // User is authenticated but needs to choose workspace
              isLoading: false,
            });
            return true;
          }

          // Reset pagination when logging in
          useOperationStore.getState().setPage(1);
          useSchedulerStore.getState().setPage(1);
          
          set({
            user: data.user,
            isAuthenticated: true,
            isLoading: false,
            requiresTwoFactor: false,
            requiresEmail2FA: false,
            requiresWorkspaceSelection: false,
            availableWorkspaces: [],
            userId: null,
            email2FASessionId: null,
          });
          return true;
        } catch (err) {
          // Handle axios errors with API response
          let message = 'An error occurred';
          if (err && typeof err === 'object' && 'response' in err) {
            const axiosError = err as { response?: { data?: { error?: { message?: string; code?: string } } } };
            message = axiosError.response?.data?.error?.message || message;
          } else if (err instanceof Error) {
            message = err.message;
          }
          set({ error: message, isLoading: false });
          return false;
        }
      },

      verify2FA: async (code: string): Promise<boolean> => {
        const { userId, rememberMe } = get();
        if (!userId) {
          set({ error: 'User ID not found for 2FA' });
          return false;
        }

        set({ isLoading: true, error: null });
        try {
          const response = await authService.verify2FA(code, userId, rememberMe);
          
          if (!response.success) {
            set({ error: response.error?.message || '2FA verification failed', isLoading: false });
            return false;
          }

          const data = response.data as LoginResponse;
          
          // Check for workspace selection requirement
          if (data.requiresWorkspaceSelection && data.workspaces) {
            set({
              requiresWorkspaceSelection: true,
              availableWorkspaces: data.workspaces,
              user: data.user,
              isAuthenticated: true,
              isLoading: false,
              requiresTwoFactor: false,
              userId: null,
            });
            return true;
          }
          
          // Reset pagination when logging in
          useOperationStore.getState().setPage(1);
          useSchedulerStore.getState().setPage(1);
          
          set({
            user: data.user,
            isAuthenticated: true,
            isLoading: false,
            requiresTwoFactor: false,
            requiresWorkspaceSelection: false,
            availableWorkspaces: [],
            userId: null,
          });
          return true;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'An error occurred';
          set({ error: message, isLoading: false });
          return false;
        }
      },

      verifyEmail2FA: async (code: string): Promise<{ success: boolean; error?: string; attemptsRemaining?: number; isLocked?: boolean }> => {
        const { userId, email2FASessionId, rememberMe } = get();
        if (!userId || !email2FASessionId) {
          return { success: false, error: 'Session not found' };
        }

        set({ isLoading: true, error: null });
        try {
          const response = await authService.verifyEmail2FA(userId, email2FASessionId, code, rememberMe);
          
          if (!response.success) {
            const errorData = response.error as { code?: string; message?: string; attemptsRemaining?: number };
            const isLocked = errorData?.code === 'ACCOUNT_LOCKED';
            set({ isLoading: false });
            return { 
              success: false, 
              error: errorData?.message || 'Verification failed',
              attemptsRemaining: errorData?.attemptsRemaining,
              isLocked,
            };
          }

          const data = response.data as LoginResponse;
          
          // Check for workspace selection requirement
          if (data.requiresWorkspaceSelection && data.workspaces) {
            set({
              requiresWorkspaceSelection: true,
              availableWorkspaces: data.workspaces,
              user: data.user,
              isAuthenticated: true,
              isLoading: false,
              requiresEmail2FA: false,
              userId: null,
              email2FASessionId: null,
            });
            return { success: true };
          }
          
          // Reset pagination when logging in
          useOperationStore.getState().setPage(1);
          useSchedulerStore.getState().setPage(1);
          
          set({
            user: data.user,
            isAuthenticated: true,
            isLoading: false,
            requiresEmail2FA: false,
            requiresWorkspaceSelection: false,
            availableWorkspaces: [],
            userId: null,
            email2FASessionId: null,
          });
          return { success: true };
        } catch (err) {
          const message = err instanceof Error ? err.message : 'An error occurred';
          set({ isLoading: false });
          return { success: false, error: message };
        }
      },

      resendEmail2FA: async (): Promise<{ success: boolean; error?: string }> => {
        const { userId, email2FASessionId } = get();
        if (!userId || !email2FASessionId) {
          return { success: false, error: 'Session not found' };
        }

        try {
          const response = await authService.resendEmail2FA(userId, email2FASessionId);
          
          if (!response.success) {
            return { 
              success: false, 
              error: response.error?.message || 'Failed to resend code',
            };
          }

          return { success: true };
        } catch (err) {
          const message = err instanceof Error ? err.message : 'An error occurred';
          return { success: false, error: message };
        }
      },

      selectWorkspace: async (workspaceId: string): Promise<boolean> => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.selectWorkspace(workspaceId);
          
          if (!response.success) {
            set({ error: response.error?.message || 'Workspace selection failed', isLoading: false });
            return false;
          }

          // Update sharedAccess info from the response
          const { user } = get();
          if (user && response.data?.sharedAccess !== undefined) {
            set({
              user: {
                ...user,
                sharedAccess: response.data.sharedAccess,
              },
            });
          }

          // Reset pagination when changing workspace
          useOperationStore.getState().setPage(1);
          useSchedulerStore.getState().setPage(1);
          
          set({
            requiresWorkspaceSelection: false,
            availableWorkspaces: [],
            isLoading: false,
          });
          return true;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'An error occurred';
          set({ error: message, isLoading: false });
          return false;
        }
      },

      logout: async (): Promise<void> => {
        try {
          await authService.logout();
        } finally {
          // Reset all related stores
          useAccountStore.getState().reset();
          useOperationStore.getState().setPage(1);
          useSchedulerStore.getState().setPage(1);
          useUserSettingsStore.getState().reset();
          useProjectStore.getState().resetStore();
          
          // Force light mode on logout
          document.documentElement.classList.remove('dark');
          
          // Clear tokens (already done in authService.logout but ensure it's done)
          clearTokens();
          
          set({
            user: null,
            isAuthenticated: false,
            requiresTwoFactor: false,
            requiresEmail2FA: false,
            requiresWorkspaceSelection: false,
            availableWorkspaces: [],
            userId: null,
            email2FASessionId: null,
            rememberMe: false,
            error: null,
          });
        }
      },

      register: async (email: string, password: string, language: 'fr' | 'en' = 'fr'): Promise<boolean> => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.register({ email, password, language });
          
          if (!response.success) {
            set({ error: response.error?.message || 'Registration failed', isLoading: false });
            return false;
          }

          set({ isLoading: false });
          return true;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'An error occurred';
          set({ error: message, isLoading: false });
          return false;
        }
      },

      checkAuth: async (): Promise<void> => {
        const token = getToken();
        
        if (!token) {
          // No token means we can't authenticate - reset state
          set({ isAuthenticated: false, user: null, isLoading: false });
          return;
        }

        // We have a token, validate it with the server
        set({ isLoading: true });
        try {
          const response = await authService.getCurrentUser();
          if (response.success && response.data) {
            set({
              user: response.data,
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            // Token exists but validation failed - token is invalid, clear it
            clearTokens();
            set({ isAuthenticated: false, user: null, isLoading: false });
          }
        } catch {
          // Network error - token might still be valid, keep current state if authenticated
          const currentState = get();
          if (currentState.isAuthenticated && currentState.user) {
            // Keep authenticated state on network error (offline support)
            set({ isLoading: false });
          } else {
            // No previous auth state, reset
            set({ isAuthenticated: false, user: null, isLoading: false });
          }
        }
      },

      clearError: () => set({ error: null }),

      resetEmail2FAState: () => set({ 
        requiresEmail2FA: false, 
        userId: null, 
        email2FASessionId: null,
        error: null,
      }),

      resetWorkspaceSelectionState: () => set({ 
        requiresWorkspaceSelection: false, 
        availableWorkspaces: [],
        error: null,
      }),

      refreshUser: async (): Promise<User | null> => {
        try {
          const response = await authService.getCurrentUser();
          if (response.success && response.data) {
            set({
              user: response.data,
              isAuthenticated: true,
            });
            return response.data;
          }
          return null;
        } catch {
          return null;
        }
      },

      updateSharedAccess: (sharedAccess: User['sharedAccess']) => {
        const { user } = get();
        if (user) {
          set({
            user: {
              ...user,
              sharedAccess,
            },
          });
        }
      },

      isPremium: () => {
        const { user } = get();
        // For secondary users, use the effective role from the owner
        if (user?.sharedAccess?.effectiveRole) {
          return user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
        }
        return user?.role === UserRole.PREMIUM || user?.role === UserRole.ADMIN;
      },

      isAdmin: () => {
        const { user } = get();
        // Secondary users are never admins themselves, even if owner is admin
        if (user?.sharedAccess) {
          return false;
        }
        return user?.role === UserRole.ADMIN;
      },

      isSecondaryUser: () => {
        const { user } = get();
        return user?.sharedAccess !== null && user?.sharedAccess !== undefined;
      },

      isOnSharedWorkspace: () => {
        const { user } = get();
        return user?.sharedAccess !== null && user?.sharedAccess !== undefined;
      },

      // Categories permissions
      canViewCategories: () => {
        const { user } = get();
        if (!user) return true;
        if (user.sharedAccess) {
          const perm = user.sharedAccess.featurePermissions?.categories;
          return perm === 'read' || perm === 'write';
        }
        // All primary users can view categories
        return true;
      },

      canManageCategories: () => {
        const { user } = get();
        if (!user) return true;
        if (user.sharedAccess) {
          // Use new featurePermissions if available, fallback to legacy
          const perm = user.sharedAccess.featurePermissions?.categories;
          if (perm !== undefined) return perm === 'write';
          return user.sharedAccess.canManageCategories === true;
        }
        if (user.role === UserRole.FREE) return false;
        return true;
      },

      // Payment Methods permissions
      canViewPaymentMethods: () => {
        const { user } = get();
        if (!user) return true;
        if (user.sharedAccess) {
          const perm = user.sharedAccess.featurePermissions?.paymentMethods;
          return perm === 'read' || perm === 'write';
        }
        return true;
      },

      canManagePaymentMethods: () => {
        const { user } = get();
        if (!user) return true;
        if (user.sharedAccess) {
          const perm = user.sharedAccess.featurePermissions?.paymentMethods;
          if (perm !== undefined) return perm === 'write';
          return user.sharedAccess.canManagePaymentMethods === true;
        }
        if (user.role === UserRole.FREE) return false;
        return true;
      },

      // Checkbooks permissions
      canViewCheckbooks: () => {
        const { user } = get();
        if (!user) return true;
        // For shared access, check the effective role (owner's role) and permission
        if (user.sharedAccess) {
          const hasPremiumRole = user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
          if (!hasPremiumRole) return false;
          const perm = user.sharedAccess.featurePermissions?.checkbooks;
          return perm === 'read' || perm === 'write';
        }
        // For own workspace, check user's own role (premium feature)
        if (user.role === UserRole.FREE) return false;
        return true;
      },

      canManageCheckbooks: () => {
        const { user } = get();
        if (!user) return true;
        // For shared access, check the effective role (owner's role) and permission
        if (user.sharedAccess) {
          const hasPremiumRole = user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
          if (!hasPremiumRole) return false;
          const perm = user.sharedAccess.featurePermissions?.checkbooks;
          return perm === 'write';
        }
        // For own workspace, check user's own role
        if (user.role === UserRole.FREE) return false;
        return true;
      },

      // Projects permissions
      canViewProjects: () => {
        const { user } = get();
        if (!user) return true;
        // For shared access, check the effective role (owner's role) and permission
        if (user.sharedAccess) {
          const hasPremiumRole = user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
          if (!hasPremiumRole) return false;
          const perm = user.sharedAccess.featurePermissions?.projects;
          return perm === 'read' || perm === 'write';
        }
        // For own workspace, check user's own role
        if (user.role === UserRole.FREE) return false;
        return true;
      },

      canManageProjects: () => {
        const { user } = get();
        if (!user) return true;
        // For shared access, check the effective role (owner's role) and permission
        if (user.sharedAccess) {
          const hasPremiumRole = user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
          if (!hasPremiumRole) return false;
          const perm = user.sharedAccess.featurePermissions?.projects;
          return perm === 'write';
        }
        // For own workspace, check user's own role
        if (user.role === UserRole.FREE) return false;
        return true;
      },

      // Reconciliation permissions
      canViewReconciliation: () => {
        const { user } = get();
        if (!user) return true;
        // For shared access, check the effective role (owner's role) and permission
        if (user.sharedAccess) {
          const hasPremiumRole = user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
          if (!hasPremiumRole) return false;
          const perm = user.sharedAccess.featurePermissions?.reconciliation;
          return perm === 'read' || perm === 'write';
        }
        // For own workspace, check user's own role
        if (user.role === UserRole.FREE) return false;
        return true;
      },

      canManageReconciliation: () => {
        const { user } = get();
        if (!user) return true;
        // For shared access, check the effective role (owner's role) and permission
        if (user.sharedAccess) {
          const hasPremiumRole = user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
          if (!hasPremiumRole) return false;
          const perm = user.sharedAccess.featurePermissions?.reconciliation;
          return perm === 'write';
        }
        // For own workspace, check user's own role
        if (user.role === UserRole.FREE) return false;
        return true;
      },

      // Keep legacy canUseReconciliation for backward compatibility
      canUseReconciliation: () => {
        const { user } = get();
        if (!user) return true;
        // For shared access, check the effective role (owner's role) and permission
        if (user.sharedAccess) {
          const hasPremiumRole = user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
          if (!hasPremiumRole) return false;
          const perm = user.sharedAccess.featurePermissions?.reconciliation;
          if (perm !== undefined) return perm === 'write';
          // Legacy: check if has write access on at least one account
          const accountPermissions = user.sharedAccess.accountPermissions;
          if (!accountPermissions || accountPermissions.length === 0) return true;
          return accountPermissions.some((p) => p.permission === 'write');
        }
        // For own workspace, check user's own role
        if (user.role === UserRole.FREE) return false;
        return true;
      },

      // Statements (Bilans) permissions
      canViewStatements: () => {
        const { user } = get();
        if (!user) return true;
        // For shared access, check the effective role (owner's role) and permission
        if (user.sharedAccess) {
          const hasPremiumRole = user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
          if (!hasPremiumRole) return false;
          const perm = user.sharedAccess.featurePermissions?.statements;
          return perm === 'read' || perm === 'write';
        }
        // For own workspace, check user's own role
        if (user.role === UserRole.FREE) return false;
        return true;
      },

      canManageStatements: () => {
        const { user } = get();
        if (!user) return true;
        // For shared access, check the effective role (owner's role) and permission
        if (user.sharedAccess) {
          const hasPremiumRole = user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
          if (!hasPremiumRole) return false;
          const perm = user.sharedAccess.featurePermissions?.statements;
          return perm === 'write';
        }
        // For own workspace, check user's own role
        if (user.role === UserRole.FREE) return false;
        return true;
      },

      // Budgets permissions
      canViewBudgets: () => {
        const { user } = get();
        if (!user) return true;
        // For shared access, check the effective role (owner's role) and permission
        if (user.sharedAccess) {
          const hasPremiumRole = user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
          if (!hasPremiumRole) return false;
          const perm = user.sharedAccess.featurePermissions?.budgets;
          return perm === 'read' || perm === 'write';
        }
        // For own workspace, check user's own role
        if (user.role === UserRole.FREE) return false;
        return true;
      },

      canManageBudgets: () => {
        const { user } = get();
        if (!user) return true;
        // For shared access, check the effective role (owner's role) and permission
        if (user.sharedAccess) {
          const hasPremiumRole = user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
          if (!hasPremiumRole) return false;
          const perm = user.sharedAccess.featurePermissions?.budgets;
          return perm === 'write';
        }
        // For own workspace, check user's own role
        if (user.role === UserRole.FREE) return false;
        return true;
      },

      // API Keys permissions
      canViewApiKeys: () => {
        const { user } = get();
        if (!user) return true;
        
        // For shared access, check the effective role (owner's role) and permission
        if (user.sharedAccess) {
          const hasPremiumRole = user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
          if (!hasPremiumRole) return false;
          const perm = user.sharedAccess.featurePermissions?.apiKeys;
          return perm === 'read' || perm === 'write';
        }
        
        // For own workspace, check user's own role
        if (user.role === UserRole.FREE) return false;
        return true;
      },

      canManageApiKeys: () => {
        const { user } = get();
        if (!user) return true;
        
        // For shared access, check the effective role (owner's role) and permission
        if (user.sharedAccess) {
          const hasPremiumRole = user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
          if (!hasPremiumRole) return false;
          const perm = user.sharedAccess.featurePermissions?.apiKeys;
          return perm === 'write';
        }
        
        // For own workspace, check user's own role
        if (user.role === UserRole.FREE) return false;
        return true;
      },

      // Alerts permissions
      // Alerts permissions
      // canViewAlerts: always true - users can always see their own alerts
      canViewAlerts: () => {
        return true;
      },

      // canManageAlerts: true if user can modify OWNER's alerts (not their own - they always can)
      canManageAlerts: () => {
        const { user } = get();
        if (!user) return true;
        if (user.sharedAccess) {
          const perm = user.sharedAccess.featurePermissions?.alerts;
          return perm === 'write';
        }
        return true;
      },

      // canViewOwnerAlerts: true if user can see owner's alerts (read or write permission)
      canViewOwnerAlerts: () => {
        const { user } = get();
        if (!user) return true;
        if (user.sharedAccess) {
          const perm = user.sharedAccess.featurePermissions?.alerts;
          return perm === 'read' || perm === 'write';
        }
        return true;
      },

      // Counterparties (tiers) permissions
      // canViewCounterparties: true if user can view counterparties (read or write permission)
      // Note: Also requires at least read access on at least one account
      canViewCounterparties: () => {
        const { user } = get();
        if (!user) return true;
        if (user.sharedAccess) {
          const perm = user.sharedAccess.featurePermissions?.counterparties;
          if (perm === 'none' || perm === undefined) return false;
          // Also need at least one account with read or write access
          const hasAccountAccess = user.sharedAccess.accountPermissions?.some(
            ap => ap.permission === 'read' || ap.permission === 'write'
          );
          return hasAccountAccess && (perm === 'read' || perm === 'write');
        }
        return true;
      },

      // canManageCounterparties: true if user can create/edit/delete/merge counterparties
      // Note: Requires write permission on counterparties AND write access on at least one account
      canManageCounterparties: () => {
        const { user } = get();
        if (!user) return true;
        if (user.sharedAccess) {
          const perm = user.sharedAccess.featurePermissions?.counterparties;
          if (perm !== 'write') return false;
          // Also need at least one account with write access
          const hasWriteAccountAccess = user.sharedAccess.accountPermissions?.some(
            ap => ap.permission === 'write'
          );
          return hasWriteAccountAccess;
        }
        return true;
      },

      canManageSchedulers: () => {
        const { user } = get();
        if (!user) return true;
        // For shared access, check the effective role (owner's role)
        if (user.sharedAccess) {
          return user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
        }
        // For own workspace, check user's own role
        if (user.role === UserRole.FREE) return false;
        return true;
      },

      canManageSharedAccess: () => {
        const { user } = get();
        if (!user) return true;
        // Shared access users cannot manage shared access
        if (user.sharedAccess) return false;
        // For own workspace, check user's own role (premium feature)
        if (user.role === UserRole.FREE) return false;
        return true;
      },
    }),
    {
      name: 'budgira-auth',
      storage: createJSONStorage(() => authStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        rememberMe: state.rememberMe,
      }),
      onRehydrateStorage: () => {
        return (_state, error) => {
          if (error) {
            console.error('Error rehydrating auth store:', error);
          }
        };
      },
    }
  )
);

// Helper to wait for hydration
export const waitForHydration = () => {
  return new Promise<void>((resolve) => {
    const unsubFinishHydration = useAuthStore.persist.onFinishHydration(() => {
      unsubFinishHydration();
      resolve();
    });
    
    // If already hydrated, resolve immediately
    if (useAuthStore.persist.hasHydrated()) {
      unsubFinishHydration();
      resolve();
    }
  });
};

export default useAuthStore;
